import baseConfig from '@config/base.config';
import analyticsService from '@assets/services/analytics.service';
import authorityUtils from '@utils/authority';
import urlUtils from '@utils/url.utils';
import cookieUtils from '@utils/cookie.utils';
import localsService from '@assets/services/locals.service';

export interface Response {
    id: string;
    ptiUid?: string;
    firstName: string;
    name: string;
    email: string;
    password: string;
    phone: string;
    industry: string;
    company: string;
    position: string;
    websiteType: string;
    token: string;
    hasActivated: boolean | null;
    hasAllow: boolean | null;
    createTime: number;
    activatedTime: number;
    updateTime: number;
    signupSource: 'ptengine' | 'ptx'; //2021年03月29日 又加了一个新官网的字段
    permissionsDict?: any;
    permissions?: any;
    i18n: 'EN' | 'JP' | 'ZH';
    status: 0 | 1 | 2;
    freeTrialLong: number;
    accountType: string;
    department: string;
    companySize: string;
    address: string;
    onlyPti: Boolean;
    jobTitle: string;
    loginUtm: string;
    needSecondVerification?: boolean;
    beFirstLogin: boolean;
    isProductIntroPopup?: boolean;
    needGuide: Boolean;
    ptiToken?: string;
    ptiPwd?: string;
    role: string;
    area: 'EN' | 'JP';
    useType: number;
    beFromGoogle: number;
}

/**
 * Update user information to local
 * @param userInfo
 * @param rememberMe
 */
export const updateLocalUserInfo = (
    userInfo: Response,
    rememberMe?: boolean,
    password?: string,
    adminEmail?: string
) => {
    const domain = urlUtils.getDomain(location.host);
    const { token, email, id, ptiUid, ptiPwd, i18n, signupSource, name, area } = userInfo;
    const {
        ACCOUNT_ID,
        ACCOUNT_EMAIL,
        ACCOUNT_TOKEN,
        ACCOUNT_SIGNUP_SOURCE,
        ACCOUNT_REGION,
        ACCOUNT_I18N,
        ACCOUNT_USERNAME,
        PTI_UID,
        SUPER_ACCOUNT,
        CookieRememberTime
    } = baseConfig;

    [
        {
            name: ACCOUNT_ID,
            value: id
        },
        {
            name: ACCOUNT_EMAIL,
            value: email
        },
        {
            name: ACCOUNT_TOKEN,
            value: token
        },
        {
            name: ACCOUNT_SIGNUP_SOURCE,
            value: signupSource
        },
        {
            name: ACCOUNT_REGION,
            value: area?.toLocaleUpperCase()
        },
        {
            name: ACCOUNT_I18N,
            value: i18n
        },
        {
            name: ACCOUNT_USERNAME,
            value: name
        },
        {
            name: PTI_UID,
            value: ptiUid
        }
    ].forEach(item => {
        const { name, value }: { name: string; value?: string } = item;
        if (value) {
            // bSecure： 针对app_auth.html中获取cookie值
            cookieUtils.setItem({
                sKey: name,
                sValue: value,
                sDomain: domain,
                vEnd: CookieRememberTime
            });
        }
    });

    // bSecure： 针对app_auth.html中获取cookie值
    adminEmail &&
        cookieUtils.setItem({
            sKey: SUPER_ACCOUNT,
            sValue: adminEmail,
            sDomain: domain,
            vEnd: CookieRememberTime
        });
    authorityUtils.loginAuthority(userInfo);
    analyticsService.identifyUser(userInfo);
    saveUserInfoToLocalStorage(userInfo, adminEmail);
};

/**
 * 存储用户信息至localStore内，方便auth页面在iframe内获取
 */
function saveUserInfoToLocalStorage(userInfo: Response, adminEmail: string = '') {
    const { id, name, email, token, i18n, role, area, useType } = userInfo;
    localStorage.setItem(
        baseConfig.USER_INFO,
        JSON.stringify({
            id,
            name,
            email,
            token: token || cookieUtils.getItem(baseConfig.ACCOUNT_TOKEN),
            i18n,
            role,
            area,
            useType,
            adminEmail: adminEmail || cookieUtils.getItem(baseConfig.SUPER_ACCOUNT)
        })
    );
}

/**
 * Verify login status
 */
export const hasLoggedIn = function () {
    const token = getTokenForApplication();
    return Boolean(token);
};

export const getTokenForApplication = function (): string | null {
    return cookieUtils.getItem(baseConfig.ACCOUNT_TOKEN);
};

export const updatePtiGtmCookie = (userInfo: Response) => {
    const domain = urlUtils.getDomain(location.host);
    const { email, ptiUid } = userInfo;
    const { ACCOUNT_ID, ACCOUNT_EMAIL, PTI_UID, CookieRememberTime } = baseConfig;
    [
        {
            name: ACCOUNT_ID,
            value: ptiUid
        },
        {
            name: PTI_UID,
            value: ptiUid
        },
        {
            name: ACCOUNT_EMAIL,
            value: email
        }
    ].forEach(item => {
        const { name, value }: { name: string; value?: string } = item;
        if (value) {
            cookieUtils.setItem({
                sKey: name,
                sValue: value,
                sDomain: domain,
                vEnd: CookieRememberTime
            });
        }
    });
};

/**
 * Clear the current user's local storage information
 */
export const clearLocalUserInfo = function (source) {
    //console.log('clearLocalUserInfo ---->', source);
    const {
        ACCOUNT_ID,
        LAST_PROFILE_ID,
        ACCOUNT_EMAIL,
        ACCOUNT_TOKEN,
        ACCOUNT_SIGNUP_SOURCE,
        SUPER_ACCOUNT,
        ACCOUNT_REGION,
        PTI_UID,
        USER_INFO
    } = baseConfig;
    [
        ACCOUNT_ID,
        LAST_PROFILE_ID,
        ACCOUNT_EMAIL,
        ACCOUNT_TOKEN,
        ACCOUNT_SIGNUP_SOURCE,
        SUPER_ACCOUNT,
        ACCOUNT_REGION,
        PTI_UID
    ].forEach(item => {
        const domain = urlUtils.getDomain(location.host);
        cookieUtils.removeItem(item, domain);

        //一并删除子域名下的cookie
        if (cookieUtils.getItem(item)) cookieUtils.removeItem(item);
        if (localStorage.getItem(item)) localStorage.removeItem(item);
    });
    localsService.removeStorageLanguage();
    localStorage.removeItem(USER_INFO);
};

export const updateLocalToken = function (token: string, uid: string) {
    const domain = urlUtils.getDomain(location.host);
    const { ACCOUNT_TOKEN, ACCOUNT_ID, CookieRememberTime } = baseConfig;
    cookieUtils.setItem({
        sKey: ACCOUNT_TOKEN,
        sValue: token,
        sDomain: domain,
        vEnd: CookieRememberTime
    });
    cookieUtils.setItem({
        sKey: ACCOUNT_ID,
        sValue: uid,
        sDomain: domain,
        vEnd: CookieRememberTime
    });
};

export const getAccountId = () =>
    (localStorage.getItem(baseConfig.ACCOUNT_ID) as string) ||
    cookieUtils.getItem(baseConfig.ACCOUNT_ID);

export const clearPtiTimeInfo = function () {
    localStorage.removeItem(baseConfig.PTI_TIME_INFO); // 清空pti时间缓存 （切换档案、退出登录时需清除）
};

/**
 * 缓存pti的日历时间 刷新时维态
 * @param info
 */
export const setPtiTimeInfoToLocalStorage = function (info: {
    starttime?: number;
    endtime?: number;
    starttimestr?: string;
    endtimestr?: string;
}) {
    const originInfo = localStorage.getItem(baseConfig.PTI_TIME_INFO);
    const result = (originInfo && JSON.parse(originInfo)) || {};
    localStorage.setItem(
        baseConfig.PTI_TIME_INFO,
        JSON.stringify({
            ...result,
            ...info
        })
    );
};
